import { onlyDigits } from '@/shared/lib/format';
import { isValidCpf } from '@/shared/lib/cpf';
import { isValidCnpj } from '@/shared/lib/cnpj';
import { isValidEmail, isValidPhone } from '@/features/nps/public-survey/partition-questions';
import type { PassbookAddField } from '@/shared/types/passbook';
import { isValidCarPlate } from './car-plate';
import { isValidIntlPhoneDigits } from './intl-phone';
import type { InviteFieldPlan } from './invite-variant';

/** Valores exatos do select — legacy/fixe-link-2 views/invite.ejs. */
export const GENDER_OPTIONS = [
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' },
  { value: 'O', label: 'Outros' },
  { value: 'P', label: 'Prefiro não dizer' },
] as const;

export interface InviteFormState {
  name: string;
  uniqueId: string;
  email: string;
  birthday: string;
  phone: string;
  gender: string;
  termsAccepted: boolean;
  /** DDI (ex.: "351") — variação `intl-phone`. */
  countryCode: string;
  /** Estado/cidade — variação com `showStateCity` (vipcell). */
  state: string;
  city: string;
  /** Valores de `passbook.addFields`, por slug. */
  customFields: Record<string, string>;
  /** Nomes dos filhos (`showChildrenGroup` — circus), lista dinâmica add/remove. */
  children: string[];
  /** Honeypot — nunca preenchido por um humano. */
  website: string;
}

export function emptyInviteForm(overrides?: Partial<InviteFormState>): InviteFormState {
  return {
    name: '',
    uniqueId: '',
    email: '',
    birthday: '',
    phone: '',
    gender: '',
    termsAccepted: false,
    countryCode: '351',
    state: '',
    city: '',
    customFields: {},
    children: [''],
    website: '',
    ...overrides,
  };
}

const BIRTHDAY_RE = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/;

/**
 * Só formato — igual ao legado, que não valida data real nem idade mínima
 * (não existe essa regra em nenhum lugar do form/backend original).
 */
export function isValidBirthdayFormat(value: string): boolean {
  return BIRTHDAY_RE.test(value);
}

export interface InviteFormValidation {
  valid: boolean;
  message?: string;
}

/**
 * Replica `validacampos()` + `deRequire()` de legacy/fixe-link-2 views/invite.ejs
 * — variação por variação (ver model/invite-variant.ts).
 */
export function validateInviteForm(
  form: InviteFormState,
  uniqueIdLabel: string,
  plan: InviteFieldPlan,
  addFields: PassbookAddField[] = [],
): InviteFormValidation {
  if (!form.name.trim()) return { valid: false, message: 'Preencha seu nome.' };

  if (plan.identity === 'cpf' && !isValidCpf(form.uniqueId)) {
    return { valid: false, message: `${uniqueIdLabel} inválido, confira o número digitado.` };
  }
  if (plan.identity === 'cnpj' && !isValidCnpj(form.uniqueId)) {
    return { valid: false, message: 'CNPJ inválido, confira o número digitado.' };
  }
  if (plan.identity === 'car-plate' && !isValidCarPlate(form.uniqueId)) {
    return { valid: false, message: 'Placa inválida, confira o número digitado.' };
  }
  if (plan.identity === 'intl-phone' && !isValidIntlPhoneDigits(onlyDigits(form.phone))) {
    return { valid: false, message: 'Telemóvel precisa ser preenchido corretamente.' };
  }

  if (!isValidEmail(form.email)) return { valid: false, message: 'Preencha um e-mail válido.' };

  if (plan.showBirthday && !isValidBirthdayFormat(form.birthday)) {
    return { valid: false, message: 'Preencha sua data de nascimento (DD/MM/AAAA).' };
  }
  if (plan.identity !== 'intl-phone' && !isValidPhone(form.phone)) {
    return { valid: false, message: 'Preencha seu celular com DDD.' };
  }
  if (plan.showGender && !form.gender) {
    return { valid: false, message: 'Gênero precisa ser preenchido corretamente.' };
  }
  if (plan.showStateCity) {
    if (!form.state) return { valid: false, message: 'Estado precisa ser preenchido corretamente.' };
    if (!form.city) return { valid: false, message: 'Cidade precisa ser preenchido corretamente.' };
  }
  for (const field of addFields) {
    if (field.required && !field.highlight && !(form.customFields[field.slug] ?? '').trim()) {
      return { valid: false, message: `Preencha o campo: ${field.label}.` };
    }
  }
  if (!form.termsAccepted) {
    return { valid: false, message: 'Obrigatório aceitar os termos e condições do regulamento.' };
  }
  return { valid: true };
}

export interface InviteRegisterPayloadFields {
  name: string;
  uniqueId: string;
  email: string;
  birthday: string;
  phone: string;
  gender: string;
  codpais?: string;
  hasAddFields?: boolean;
  /**
   * Legado serializa filhos como ARRAY de objetos de uma chave cada
   * (`[{nomeFilho_0: "..."}, {nomeFilho_1: "..."}]`, ver `generateInvite()`
   * em routes/invite.js) — diferente do objeto plano usado por addFields/
   * estado-cidade. Replicamos os dois formatos por fidelidade.
   */
  fields?: Record<string, string> | Array<Record<string, string>>;
  website?: string;
}

/** Monta o payload de `/register` a partir do form + variação — decide o formato de `uniqueId`/`phone`. */
export function buildInviteRegisterPayload(
  form: InviteFormState,
  plan: InviteFieldPlan,
  hasCustomFields: boolean,
): InviteRegisterPayloadFields {
  let fields: Record<string, string> | Array<Record<string, string>> | undefined;
  if (plan.showChildrenGroup) {
    const names = form.children.map((n) => n.trim()).filter(Boolean);
    if (names.length > 0) fields = names.map((name, i) => ({ [`nomeFilho_${i}`]: name }));
  } else if (hasCustomFields || plan.showStateCity) {
    const flat: Record<string, string> = { ...form.customFields };
    if (plan.showStateCity) {
      flat.state = form.state;
      flat.city = form.city;
    }
    fields = flat;
  }

  const base = {
    name: form.name.trim(),
    email: form.email.trim(),
    birthday: plan.showBirthday ? form.birthday : '',
    gender: plan.showGender ? form.gender : '',
    website: form.website,
    ...(fields ? { hasAddFields: true, fields } : {}),
  };

  if (plan.identity === 'cnpj') {
    return { ...base, uniqueId: onlyDigits(form.uniqueId), phone: form.phone };
  }
  if (plan.identity === 'car-plate') {
    return { ...base, uniqueId: form.uniqueId, phone: form.phone };
  }
  if (plan.identity === 'intl-phone') {
    const digits = onlyDigits(form.phone);
    return {
      ...base,
      uniqueId: `${form.countryCode}${digits}`,
      phone: digits,
      codpais: form.countryCode,
    };
  }
  return { ...base, uniqueId: onlyDigits(form.uniqueId), phone: form.phone };
}
